import { forwardRef, type HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Pill-shaped Badge for labels, status indicators, counts.
 *
 * Variants pair a tinted background with a darker-toned text in the
 * same palette family, e.g. `bg-success-50 text-success-700`. All
 * variants are theme-aware: the tints flip in dark mode so the badge
 * stays legible on dark surfaces.
 */
export const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-full font-medium whitespace-nowrap',
  {
    variants: {
      variant: {
        default: 'bg-ink-100 text-ink-700',
        primary: 'bg-accent-50 text-accent-700',
        success: 'bg-success-50 text-success-700',
        warning: 'bg-warning-50 text-warning-600',
        danger: 'bg-danger-50 text-danger-600',
        info: 'bg-info-50 text-info-600',
        mint: 'bg-accent-mint-50 text-accent-mint-600',
        outline: 'border border-ink-200 text-ink-700',
      },
      size: {
        sm: 'px-1.5 py-0.5 text-xs',
        md: 'px-2 py-0.5 text-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'sm',
    },
  }
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, size, ...props }, ref) => (
    <span ref={ref} className={cn(badgeVariants({ variant, size }), className)} {...props} />
  )
);
Badge.displayName = 'Badge';
